import React from 'react';

// ── US sport team logos via ESPN CDN ────────────────────────────────────────

const TEAM_ABBR = {
  // NBA
  'Atlanta Hawks': 'atl', 'Boston Celtics': 'bos', 'Brooklyn Nets': 'bkn',
  'Charlotte Hornets': 'cha', 'Chicago Bulls': 'chi', 'Cleveland Cavaliers': 'cle',
  'Dallas Mavericks': 'dal', 'Denver Nuggets': 'den', 'Detroit Pistons': 'det',
  'Golden State Warriors': 'gs', 'Houston Rockets': 'hou', 'Indiana Pacers': 'ind',
  'LA Clippers': 'lac', 'Los Angeles Clippers': 'lac', 'Los Angeles Lakers': 'lal',
  'Memphis Grizzlies': 'mem', 'Miami Heat': 'mia', 'Milwaukee Bucks': 'mil',
  'Minnesota Timberwolves': 'min', 'New Orleans Pelicans': 'no', 'New York Knicks': 'ny',
  'Oklahoma City Thunder': 'okc', 'Orlando Magic': 'orl', 'Philadelphia 76ers': 'phi',
  'Phoenix Suns': 'phx', 'Portland Trail Blazers': 'por', 'Sacramento Kings': 'sac',
  'San Antonio Spurs': 'sa', 'Toronto Raptors': 'tor', 'Utah Jazz': 'uta',
  'Washington Wizards': 'wsh',

  // NFL
  'Arizona Cardinals': 'ari', 'Atlanta Falcons': 'atl', 'Baltimore Ravens': 'bal',
  'Buffalo Bills': 'buf', 'Carolina Panthers': 'car', 'Chicago Bears': 'chi',
  'Cincinnati Bengals': 'cin', 'Cleveland Browns': 'cle', 'Dallas Cowboys': 'dal',
  'Denver Broncos': 'den', 'Detroit Lions': 'det', 'Green Bay Packers': 'gb',
  'Houston Texans': 'hou', 'Indianapolis Colts': 'ind', 'Jacksonville Jaguars': 'jax',
  'Kansas City Chiefs': 'kc', 'Las Vegas Raiders': 'lv', 'Los Angeles Chargers': 'lac',
  'Los Angeles Rams': 'lar', 'Miami Dolphins': 'mia', 'Minnesota Vikings': 'min',
  'New England Patriots': 'ne', 'New Orleans Saints': 'no', 'New York Giants': 'nyg',
  'New York Jets': 'nyj', 'Philadelphia Eagles': 'phi', 'Pittsburgh Steelers': 'pit',
  'San Francisco 49ers': 'sf', 'Seattle Seahawks': 'sea', 'Tampa Bay Buccaneers': 'tb',
  'Tennessee Titans': 'ten', 'Washington Commanders': 'wsh',

  // MLB
  'Arizona Diamondbacks': 'ari', 'Athletics': 'ath', 'Oakland Athletics': 'ath',
  'Las Vegas Athletics': 'ath', 'Atlanta Braves': 'atl', 'Baltimore Orioles': 'bal',
  'Boston Red Sox': 'bos', 'Chicago Cubs': 'chc', 'Chicago White Sox': 'chw',
  'Cincinnati Reds': 'cin', 'Cleveland Guardians': 'cle', 'Colorado Rockies': 'col',
  'Detroit Tigers': 'det', 'Houston Astros': 'hou', 'Kansas City Royals': 'kc',
  'Los Angeles Angels': 'laa', 'Los Angeles Dodgers': 'lad', 'Miami Marlins': 'mia',
  'Milwaukee Brewers': 'mil', 'Minnesota Twins': 'min', 'New York Mets': 'nym',
  'New York Yankees': 'nyy', 'Philadelphia Phillies': 'phi', 'Pittsburgh Pirates': 'pit',
  'San Diego Padres': 'sd', 'San Francisco Giants': 'sf', 'Seattle Mariners': 'sea',
  'St. Louis Cardinals': 'stl', 'Tampa Bay Rays': 'tb', 'Texas Rangers': 'tex',
  'Toronto Blue Jays': 'tor', 'Washington Nationals': 'wsh',

  // NHL
  'Anaheim Ducks': 'ana', 'Boston Bruins': 'bos', 'Buffalo Sabres': 'buf',
  'Calgary Flames': 'cgy', 'Carolina Hurricanes': 'car', 'Chicago Blackhawks': 'chi',
  'Colorado Avalanche': 'col', 'Columbus Blue Jackets': 'cbj', 'Dallas Stars': 'dal',
  'Detroit Red Wings': 'det', 'Edmonton Oilers': 'edm', 'Florida Panthers': 'fla',
  'Los Angeles Kings': 'la', 'Minnesota Wild': 'min', 'Montreal Canadiens': 'mtl',
  'Nashville Predators': 'nsh', 'New Jersey Devils': 'nj', 'New York Islanders': 'nyi',
  'New York Rangers': 'nyr', 'Ottawa Senators': 'ott', 'Philadelphia Flyers': 'phi',
  'Pittsburgh Penguins': 'pit', 'San Jose Sharks': 'sj', 'Seattle Kraken': 'sea',
  'St. Louis Blues': 'stl', 'Tampa Bay Lightning': 'tb', 'Toronto Maple Leafs': 'tor',
  'Utah Hockey Club': 'utah', 'Vancouver Canucks': 'van', 'Vegas Golden Knights': 'vgk',
  'Washington Capitals': 'wsh', 'Winnipeg Jets': 'wpg',

  // WNBA
  'Atlanta Dream': 'atl', 'Chicago Sky': 'chi', 'Connecticut Sun': 'con',
  'Dallas Wings': 'dal', 'Golden State Valkyries': 'gs', 'Indiana Fever': 'ind',
  'Las Vegas Aces': 'lv', 'Los Angeles Sparks': 'la', 'Minnesota Lynx': 'min',
  'New York Liberty': 'ny', 'Phoenix Mercury': 'phx', 'Portland Fire': 'por',
  'Seattle Storm': 'sea', 'Toronto Tempo': 'tor', 'Washington Mystics': 'wsh',
};

const SPORT_PATH = {
  basketball_nba: 'nba', basketball_wnba: 'wnba',
  americanfootball_nfl: 'nfl', americanfootball_ncaaf: 'nfl',
  baseball_mlb: 'mlb', baseball_wbc: 'mlb',
  icehockey_nhl: 'nhl',
};

function getSportPath(sportKey) {
  if (!sportKey) return null;
  if (SPORT_PATH[sportKey]) return SPORT_PATH[sportKey];
  if (sportKey.includes('wnba')) return 'wnba';
  if (sportKey.includes('nba')) return 'nba';
  if (sportKey.includes('nfl') || sportKey.includes('americanfootball')) return 'nfl';
  if (sportKey.includes('mlb') || sportKey.includes('baseball')) return 'mlb';
  if (sportKey.includes('nhl') || sportKey.includes('hockey')) return 'nhl';
  return null;
}

// ── Flag emoji helpers ───────────────────────────────────────────────────────

const toFlag = (iso2) =>
  [...iso2.toUpperCase()].map(c => String.fromCodePoint(c.charCodeAt(0) + 127397)).join('');

// Country name → ISO 3166-1 alpha-2 code
const COUNTRY_ISO = {
  'Albania': 'al', 'Algeria': 'dz', 'Andorra': 'ad', 'Argentina': 'ar',
  'Armenia': 'am', 'Australia': 'au', 'Austria': 'at', 'Azerbaijan': 'az',
  'Bahrain': 'bh', 'Belarus': 'by', 'Belgium': 'be', 'Bolivia': 'bo',
  'Bosnia and Herzegovina': 'ba', 'Bosnia & Herzegovina': 'ba',
  'Brazil': 'br', 'Bulgaria': 'bg', 'Cameroon': 'cm', 'Canada': 'ca',
  'Chile': 'cl', 'China': 'cn', "Cote d'Ivoire": 'ci', "Côte d'Ivoire": 'ci',
  'Ivory Coast': 'ci', 'Colombia': 'co', 'Costa Rica': 'cr', 'Croatia': 'hr',
  'Cuba': 'cu', 'Cyprus': 'cy', 'Czech Republic': 'cz', 'Czechia': 'cz',
  'Denmark': 'dk', 'Ecuador': 'ec', 'Egypt': 'eg', 'El Salvador': 'sv',
  'England': 'gb', 'Estonia': 'ee', 'Ethiopia': 'et', 'Finland': 'fi',
  'France': 'fr', 'Georgia': 'ge', 'Germany': 'de', 'Ghana': 'gh',
  'Greece': 'gr', 'Guatemala': 'gt', 'Honduras': 'hn', 'Hungary': 'hu',
  'Iceland': 'is', 'India': 'in', 'Indonesia': 'id', 'Iran': 'ir',
  'Iraq': 'iq', 'Ireland': 'ie', 'Israel': 'il', 'Italy': 'it',
  'Jamaica': 'jm', 'Japan': 'jp', 'Jordan': 'jo', 'Kazakhstan': 'kz',
  'Kenya': 'ke', 'Kosovo': 'xk', 'Kuwait': 'kw', 'Latvia': 'lv',
  'Lebanon': 'lb', 'Libya': 'ly', 'Liechtenstein': 'li', 'Lithuania': 'lt',
  'Luxembourg': 'lu', 'Malaysia': 'my', 'Mali': 'ml', 'Malta': 'mt',
  'Mexico': 'mx', 'Moldova': 'md', 'Montenegro': 'me', 'Morocco': 'ma',
  'Netherlands': 'nl', 'New Zealand': 'nz', 'Nigeria': 'ng',
  'North Korea': 'kp', 'North Macedonia': 'mk', 'Northern Ireland': 'gb',
  'Norway': 'no', 'Oman': 'om', 'Pakistan': 'pk', 'Panama': 'pa',
  'Paraguay': 'py', 'Peru': 'pe', 'Philippines': 'ph', 'Poland': 'pl',
  'Portugal': 'pt', 'Qatar': 'qa', 'Romania': 'ro', 'Russia': 'ru',
  'Saudi Arabia': 'sa', 'Scotland': 'gb', 'Senegal': 'sn', 'Serbia': 'rs',
  'Slovakia': 'sk', 'Slovenia': 'si', 'South Africa': 'za',
  'South Korea': 'kr', 'Korea Republic': 'kr', 'Spain': 'es', 'Sweden': 'se',
  'Switzerland': 'ch', 'Syria': 'sy', 'Thailand': 'th', 'Trinidad and Tobago': 'tt',
  'Tunisia': 'tn', 'Turkey': 'tr', 'Türkiye': 'tr', 'Ukraine': 'ua',
  'United Arab Emirates': 'ae', 'United States': 'us', 'USA': 'us',
  'Uruguay': 'uy', 'Uzbekistan': 'uz', 'Venezuela': 've', 'Vietnam': 'vn',
  'Wales': 'gb', 'Zambia': 'zm', 'Zimbabwe': 'zw',
};

// Tennis player name → ISO 3166-1 alpha-2 code
const PLAYER_ISO = {
  // ATP
  'Jannik Sinner': 'it', 'Carlos Alcaraz': 'es', 'Novak Djokovic': 'rs',
  'Alexander Zverev': 'de', 'Daniil Medvedev': 'ru', 'Casper Ruud': 'no',
  'Andrey Rublev': 'ru', 'Holger Rune': 'dk', 'Hubert Hurkacz': 'pl',
  'Grigor Dimitrov': 'bg', 'Alex de Minaur': 'au', 'Taylor Fritz': 'us',
  'Ben Shelton': 'us', 'Tommy Paul': 'us', 'Frances Tiafoe': 'us',
  'Sebastian Korda': 'us', 'Reilly Opelka': 'us', 'Jack Sock': 'us',
  'Jenson Brooksby': 'us', 'Marcos Giron': 'us', 'Brandon Nakashima': 'us',
  'Maxime Cressy': 'us', 'J.J. Wolf': 'us', 'JJ Wolf': 'us',
  'Christopher Eubanks': 'us', 'Mackenzie McDonald': 'us', 'Emilio Nava': 'us',
  'Stefan Kozlov': 'us', 'Jack Draper': 'gb', 'Cameron Norrie': 'gb',
  'Andy Murray': 'gb', 'Dan Evans': 'gb', 'Liam Broady': 'gb',
  'Jan Choinski': 'gb', 'Paul Jubb': 'gb', 'Ryan Peniston': 'gb',
  'Billy Harris': 'gb', 'Alastair Gray': 'gb',
  'Rafael Nadal': 'es', 'Roberto Bautista Agut': 'es', 'Pablo Carreno Busta': 'es',
  'Alejandro Davidovich Fokina': 'es', 'Pedro Martinez': 'es',
  'Bernabe Zapata Miralles': 'es', 'Albert Ramos-Vinolas': 'es',
  'Carlos Taberner': 'es', 'Roberto Carballes Baena': 'es',
  'Jessica Bouzas Maneiro': 'es',
  'Jakub Mensik': 'cz', 'Jiri Lehecka': 'cz', 'Tomas Machac': 'cz',
  'Zdenek Kolar': 'cz', 'Dalibor Svrcina': 'cz', 'Vit Kopriva': 'cz',
  'Jan-Lennard Struff': 'de', 'Yannick Hanfmann': 'de', 'Oscar Otte': 'de',
  'Dominik Koepfer': 'de', 'Peter Gojowczyk': 'de', 'Maximilian Marterer': 'de',
  'Ugo Humbert': 'fr', 'Arthur Fils': 'fr', 'Corentin Moutet': 'fr',
  'Hugo Gaston': 'fr', 'Arthur Rinderknech': 'fr', 'Benjamin Bonzi': 'fr',
  'Terence Atmane': 'fr', 'Maxime Janvier': 'fr', 'Adrian Mannarino': 'fr',
  'Richard Gasquet': 'fr', 'Gilles Simon': 'fr', 'Pierre-Hugues Herbert': 'fr',
  'Harold Mayot': 'fr', 'Quentin Halys': 'fr', 'Luca Van Assche': 'fr',
  'Antoine Hoang': 'fr',
  'Lorenzo Musetti': 'it', 'Matteo Berrettini': 'it', 'Fabio Fognini': 'it',
  'Lorenzo Sonego': 'it', 'Luca Nardi': 'it', 'Flavio Cobolli': 'it',
  'Luciano Darderi': 'it', 'Matteo Arnaldi': 'it', 'Mattia Bellucci': 'it',
  'Stefano Travaglia': 'it', 'Andrea Vavassori': 'it', 'Giulio Zeppieri': 'it',
  'Stefanos Tsitsipas': 'gr', 'Michail Pervolarakis': 'gr',
  'Borna Coric': 'hr', 'Marin Cilic': 'hr', 'Borna Gojo': 'hr',
  'Felix Auger-Aliassime': 'ca', 'Denis Shapovalov': 'ca', 'Milos Raonic': 'ca',
  'Vasek Pospisil': 'ca', 'Gabriel Diallo': 'ca', 'Alexis Galarneau': 'ca',
  'Francisco Cerundolo': 'ar', 'Tomas Etcheverry': 'ar', 'Sebastian Baez': 'ar',
  'Pedro Cachin': 'ar', 'Mariano Navone': 'ar', 'Diego Schwartzman': 'ar',
  'Facundo Bagnis': 'ar', 'Federico Coria': 'ar', 'Andres Molteni': 'ar',
  'Camilo Ugo Carabelli': 'ar',
  'Nicolas Jarry': 'cl', 'Alejandro Tabilo': 'cl', 'Cristian Garin': 'cl',
  'Tomas Barrios Vera': 'cl', 'Marcelo Tomas Barrios': 'cl',
  'Thiago Monteiro': 'br', 'Thiago Seyboth Wild': 'br', 'Joao Fonseca': 'br',
  'Felipe Meligeni Alves': 'br', 'Pedro Araujo': 'br',
  'Stan Wawrinka': 'ch', 'Marc-Andrea Huesler': 'ch', 'Dominic Stricker': 'ch',
  'Leandro Riedi': 'ch', 'Luca Margaroli': 'ch',
  'Karen Khachanov': 'ru', 'Roman Safiullin': 'ru', 'Aslan Karatsev': 'ru',
  'Andrei Rublev': 'ru',
  'Alexander Bublik': 'kz', 'Mikhail Kukushkin': 'kz',
  'Dominic Thiem': 'at', 'Sebastian Ofner': 'at', 'Lucas Miedler': 'at',
  'Filip Misolic': 'at', 'Dennis Novak': 'at',
  'Dusan Lajovic': 'rs', 'Miomir Kecmanovic': 'rs', 'Laslo Djere': 'rs',
  'Filip Krajinovic': 'rs', 'Hamad Medjedovic': 'rs',
  'Tallon Griekspoor': 'nl', 'Botic van de Zandschulp': 'nl',
  'Jesper de Jong': 'nl',
  'Nuno Borges': 'pt', 'Joao Sousa': 'pt', 'Pedro Sousa': 'pt',
  'Frederico Silva': 'pt',
  'Elias Ymer': 'se', 'Mikael Ymer': 'se', 'Leo Borg': 'se',
  'Taro Daniel': 'jp', 'Yoshihito Nishioka': 'jp',
  'Juncheng Shang': 'cn', 'Yunchaokete Bu': 'cn', 'Wu Yibing': 'cn',
  'Zhang Zhizhen': 'cn',
  'Soonwoo Kwon': 'kr', 'Hyeon Chung': 'kr',
  'David Goffin': 'be', 'Zizou Bergs': 'be',
  'Thanasi Kokkinakis': 'au', 'Jordan Thompson': 'au', 'Alex Bolt': 'au',
  'Christopher O\'Connell': 'au', 'Alexei Popyrin': 'au',
  'Rinky Hijikata': 'au', 'Adam Walton': 'au', 'Aleksandar Vukic': 'au',
  'Jason Kubler': 'au', 'James Duckworth': 'au',
  'Daniel Elahi Galan': 'co', 'Alejandro Gonzalez': 'co',
  'Viktor Troicki': 'rs', 'Dimitar Kuzmanov': 'bg',
  'Oleksii Krutykh': 'ua', 'Illya Marchenko': 'ua',
  'Viktor Durasovic': 'no',

  // WTA
  'Aryna Sabalenka': 'by', 'Iga Swiatek': 'pl', 'Coco Gauff': 'us',
  'Elena Rybakina': 'kz', 'Jessica Pegula': 'us', 'Qinwen Zheng': 'cn',
  'Barbora Krejcikova': 'cz', 'Marketa Vondrousova': 'cz',
  'Karolina Muchova': 'cz', 'Marie Bouzkova': 'cz', 'Maria Bouzkova': 'cz',
  'Tereza Martincova': 'cz', 'Linda Noskova': 'cz', 'Karolina Pliskova': 'cz',
  'Petra Kvitova': 'cz', 'Sara Bejlek': 'cz', 'Brenda Fruhvirtova': 'cz',
  'Linda Fruhvirtova': 'cz', 'Tereza Valentova': 'cz',
  'Maria Sakkari': 'gr', 'Danielle Collins': 'us', 'Madison Keys': 'us',
  'Sloane Stephens': 'us', 'Sofia Kenin': 'us', 'Amanda Anisimova': 'us',
  'Ann Li': 'us', 'Bernarda Pera': 'us', 'Taylor Townsend': 'us',
  'Lauren Davis': 'us', 'Hailey Baptiste': 'us', 'Robin Montgomery': 'us',
  'Emma Navarro': 'us', 'Peyton Stearns': 'us', 'Caroline Dolehide': 'us',
  'Shelby Rogers': 'us', 'Alison Riske': 'us',
  'Emma Raducanu': 'gb', 'Katie Boulter': 'gb', 'Harriet Dart': 'gb',
  'Heather Watson': 'gb', 'Jodie Burrage': 'gb', 'Francesca Jones': 'gb',
  'Sonay Kartal': 'gb', 'Mimi Xu': 'gb', 'Eden Silva': 'gb',
  'Fran Jones': 'gb',
  'Ons Jabeur': 'tn', 'Naomi Osaka': 'jp',
  'Jasmine Paolini': 'it', 'Camila Giorgi': 'it', 'Sara Errani': 'it',
  'Martina Trevisan': 'it', 'Elisabetta Cocciaretto': 'it',
  'Lucia Bronzetti': 'it', 'Giulia Gatto-Monticone': 'it',
  'Paula Badosa': 'es', 'Sara Sorribes Tormo': 'es', 'Nuria Parrizas Diaz': 'es',
  'Cristina Bucsa': 'es', 'Rebeka Masarova': 'es',
  'Caroline Garcia': 'fr', 'Diane Parry': 'fr', 'Oceane Dodin': 'fr',
  'Varvara Gracheva': 'fr', 'Fiona Ferro': 'fr', 'Kristina Mladenovic': 'fr',
  'Belinda Bencic': 'ch',
  'Victoria Azarenka': 'by', 'Aliaksandra Sasnovich': 'by',
  'Bianca Andreescu': 'ca', 'Leylah Fernandez': 'ca', 'Rebecca Marino': 'ca',
  'Eugenie Bouchard': 'ca', 'Carol Zhao': 'ca', 'Gabriela Dabrowski': 'ca',
  'Elina Svitolina': 'ua', 'Dayana Yastremska': 'ua', 'Lesia Tsurenko': 'ua',
  'Marta Kostyuk': 'ua', 'Anhelina Kalinina': 'ua',
  'Veronika Kudermetova': 'ru', 'Ekaterina Alexandrova': 'ru',
  'Anastasia Pavlyuchenkova': 'ru', 'Liudmila Samsonova': 'ru',
  'Daria Kasatkina': 'ru', 'Mirra Andreeva': 'ru', 'Erika Andreeva': 'ru',
  'Diana Shnaider': 'ru', 'Anna Kalinskaya': 'ru', 'Anastasia Potapova': 'ru',
  'Anna Blinkova': 'ru', 'Vera Zvonareva': 'ru',
  'Polina Kudermetova': 'ru', 'Elina Avanesyan': 'ru', 'Anastasia Zakharova': 'ru',
  'Valeria Savinykh': 'ru', 'Kamilla Rakhimova': 'ru', 'Oksana Selekhmeteva': 'ru',
  'Laura Siegemund': 'de', 'Tatjana Maria': 'de', 'Eva Lys': 'de',
  'Jule Niemeier': 'de', 'Angelique Kerber': 'de', 'Ella Seidel': 'de',
  'Sorana Cirstea': 'ro', 'Simona Halep': 'ro', 'Elena Gabriela Ruse': 'ro',
  'Ana Bogdan': 'ro', 'Irina-Camelia Begu': 'ro', 'Jaqueline Cristian': 'ro',
  'Miriam Bulgaru': 'ro',
  'Yulia Putintseva': 'kz',
  'Donna Vekic': 'hr', 'Petra Martic': 'hr', 'Ana Konjuh': 'hr',
  'Antonia Ruzic': 'hr', 'Lucija Ciric Bagaric': 'hr',
  'Arantxa Rus': 'nl', 'Richel Hogenkamp': 'nl',
  'Ajla Tomljanovic': 'au', 'Storm Hunter': 'au', 'Daria Gavrilova': 'au',
  'Astra Sharma': 'au', 'Jaimee Fourlis': 'au', 'Maddison Inglis': 'au',
  'Destanee Aiava': 'au', 'Priscilla Hon': 'au', 'Olivia Gadecki': 'au',
  'Kimberly Birrell': 'au', 'Ellen Perez': 'au',
  'Xinyu Wang': 'cn', 'Yafan Wang': 'cn', 'Shuai Zhang': 'cn',
  'Lin Zhu': 'cn', 'Zhu Lin': 'cn', 'Xiyu Wang': 'cn', 'Peng Shuai': 'cn',
  'Tamara Zidansek': 'si', 'Kaja Juvan': 'si', 'Polona Hercog': 'si',
  'Viktoriya Tomova': 'bg',
  'Maja Chwalinska': 'pl', 'Magda Linette': 'pl', 'Katarzyna Kawa': 'pl',
  'Martyna Kubka': 'pl', 'Weronika Falkowska': 'pl',
  'Caroline Wozniacki': 'dk',

  // WTA — additional
  'Alexandra Eala': 'ph',
  'Alycia Parks': 'us', 'Claire Liu': 'us', 'Caty McNally': 'us',
  'Ashlyn Krueger': 'us', 'Lindsey Davis': 'us', 'Reese Brantmeier': 'us',
  'Nina Stojanovic': 'rs', 'Olga Danilovic': 'rs', 'Aleksandra Krunic': 'rs',
  'Ivana Jorovic': 'rs', 'Natalija Stevanovic': 'rs',
  'Lina Glushko': 'il',
  'Sofia Shapatava': 'ge',
  'Anastasia Gasanova': 'ru', 'Ekaterina Makarova': 'ru',
  'Nadia Podoroska': 'ar', 'Julia Riera': 'ar',
  'Katerina Siniakova': 'cz',
  'Viktoriya Hruncakova': 'sk', 'Rebecca Sramkova': 'sk', 'Anna Schmiedlova': 'sk',
  'Tamara Korpatsch': 'de',
  'Wang Xinyu': 'cn',

  // ATP — additional
  'Matteo Gigante': 'it', 'Francesco Passaro': 'it', 'Lorenzo Giustino': 'it',
  'Gregoire Barrere': 'fr', 'Constant Lestienne': 'fr', 'Manuel Guinard': 'fr',
  'Cassius Chaumu': 'au',
  'Marton Fucsovics': 'hu', 'Fabian Marozsan': 'hu',
  'Otto Virtanen': 'fi', 'Emil Ruusuvuori': 'fi',
};

// ── Component ────────────────────────────────────────────────────────────────

function isSoccer(sportKey) {
  return sportKey?.includes('soccer') || sportKey?.includes('football_world');
}

function isTennis(sportKey) {
  return sportKey?.includes('tennis');
}

function TeamLogo({ teamName, sportKey, size = 24, className = '' }) {
  // Soccer: show country flag emoji
  if (isSoccer(sportKey)) {
    const iso = COUNTRY_ISO[teamName];
    if (!iso) return null;
    return (
      <span className={`team-flag ${className}`} style={{ fontSize: size * 0.9, lineHeight: 1 }}>
        {toFlag(iso)}
      </span>
    );
  }

  // Tennis: show player nationality flag emoji
  if (isTennis(sportKey)) {
    const iso = PLAYER_ISO[teamName];
    if (!iso) return null;
    return (
      <span className={`team-flag ${className}`} style={{ fontSize: size * 0.9, lineHeight: 1 }}>
        {toFlag(iso)}
      </span>
    );
  }

  // US team sports: show ESPN team logo image
  const abbr = TEAM_ABBR[teamName];
  const sport = getSportPath(sportKey);
  if (!abbr || !sport) return null;

  return (
    <img
      src={`https://a.espncdn.com/i/teamlogos/${sport}/500/${abbr}.png`}
      alt={teamName}
      className={`team-logo ${className}`}
      style={{ height: size, width: size, objectFit: 'contain', flexShrink: 0 }}
      onError={(e) => { e.target.style.display = 'none'; }}
    />
  );
}

export default TeamLogo;
